/**
 * OnPremise.tsx — 프리미엄 컬러풀 레이아웃
 * Hero: 애니메이션 터미널 + 컬러 orb
 * 시험 합격: 거대한 숫자 중심
 * 비교: 컬러 카드 덱 tilt
 */
import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import ForceDarkTheme from '../../components/common/ForceDarkTheme'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  FiShield, FiLock, FiServer, FiExternalLink, FiArrowRight,
  FiCheck, FiX, FiAlertTriangle, FiAward, FiClock,
  FiFileText, FiUsers, FiZap,
} from 'react-icons/fi'
import Button from '../../components/ui/Button'
import Brand, { renderWithBrand } from '../../components/brand/Brand'

// ── 애니메이션 헬퍼 ─────────────────────────────────────────────
const inView = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-48px' },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
}

const stagger = (i: number) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-48px' },
  transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
})

// ── 터미널 타이핑 애니메이션 ────────────────────────────────────
const TERMINAL_LINE_SPECS: { key: string; delay: number; color: string; cursor?: boolean }[] = [
  { key: 'onpremise.terminal.line1', delay: 0, color: 'text-zinc-300' },
  { key: 'onpremise.terminal.line2', delay: 600, color: 'text-zinc-500' },
  { key: 'onpremise.terminal.line3', delay: 1400, color: 'text-zinc-500' },
  { key: 'onpremise.terminal.line4', delay: 2200, color: 'text-zinc-500' },
  { key: 'onpremise.terminal.line5', delay: 3000, color: 'text-emerald-400' },
  { key: 'onpremise.terminal.line6', delay: 3600, color: 'text-emerald-400' },
  { key: 'onpremise.terminal.line7', delay: 4200, color: 'text-emerald-400' },
  { key: 'onpremise.terminal.line8', delay: 4800, color: 'text-amber-400', cursor: true },
]

function TerminalMockup() {
  const { t } = useTranslation()
  const [visibleLines, setVisibleLines] = useState(0)

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = []
    TERMINAL_LINE_SPECS.forEach((line, i) => {
      timers.push(setTimeout(() => setVisibleLines(i + 1), line.delay))
    })
    return () => timers.forEach(clearTimeout)
  }, [])

  return (
    <div className="relative rounded-lg border border-emerald-500/25 bg-zinc-950/90 overflow-hidden shadow-md shadow-emerald-500/10">
      {/* 터미널 헤더 */}
      <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.08] bg-zinc-900/80">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-500/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-500/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
        <span className="ml-3 text-xs text-zinc-500 font-mono">{renderWithBrand(t('onpremise.terminal.title'))}</span>
        <span className="ml-auto flex items-center gap-1.5 text-[10px] text-emerald-400 font-mono">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
          {t('onpremise.terminal.secure')}
        </span>
      </div>

      {/* 터미널 본문 */}
      <div className="p-5 font-mono text-sm min-h-[260px] flex flex-col gap-1.5">
        {TERMINAL_LINE_SPECS.slice(0, visibleLines).map((line, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
            className={`${line.color} leading-relaxed`}
          >
            {renderWithBrand(t(line.key))}
            {line.cursor && visibleLines === TERMINAL_LINE_SPECS.length && (
              <span className="inline-block w-2 h-3.5 bg-amber-400 animate-pulse ml-0.5 align-middle" />
            )}
          </motion.div>
        ))}
      </div>
    </div>
  )
}

// ── 비교 데이터 ─────────────────────────────────────────────────
const COMPARISON_ROW_KEYS: {
  itemKey: string; gptKey: string; geminiKey: string; saifeKey: string
}[] = [
  { itemKey: 'onpremise.comparison.rows.hazardCountItem', gptKey: 'onpremise.comparison.rows.hazardCountGpt', geminiKey: 'onpremise.comparison.rows.hazardCountGemini', saifeKey: 'onpremise.comparison.rows.hazardCountHeinrich' },
  { itemKey: 'onpremise.comparison.rows.gradeItem', gptKey: 'onpremise.comparison.rows.gradeGpt', geminiKey: 'onpremise.comparison.rows.gradeGemini', saifeKey: 'onpremise.comparison.rows.gradeHeinrich' },
  { itemKey: 'onpremise.comparison.rows.caseItem', gptKey: 'onpremise.comparison.rows.caseGpt', geminiKey: 'onpremise.comparison.rows.caseGemini', saifeKey: 'onpremise.comparison.rows.caseHeinrich' },
  { itemKey: 'onpremise.comparison.rows.lawItem', gptKey: 'onpremise.comparison.rows.lawGpt', geminiKey: 'onpremise.comparison.rows.lawGemini', saifeKey: 'onpremise.comparison.rows.lawHeinrich' },
  { itemKey: 'onpremise.comparison.rows.cascadeItem', gptKey: 'onpremise.comparison.rows.cascadeGpt', geminiKey: 'onpremise.comparison.rows.cascadeGemini', saifeKey: 'onpremise.comparison.rows.cascadeHeinrich' },
]

export default function OnPremise() {
  const { t } = useTranslation()

  return (
    <>
      <ForceDarkTheme />
      <Helmet>
        <title>{t('onpremise.pageTitle')}</title>
        <meta name="description" content={t('onpremise.metaDescription')} />
        <link rel="canonical" href="https://heinrich.dasancms.co.kr/on-premise/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Heinrich" />
        <meta property="og:url" content="https://heinrich.dasancms.co.kr/on-premise/" />
        <meta property="og:title" content={t('onpremise.pageTitle')} />
        <meta property="og:description" content={t('onpremise.metaDescription')} />
        <meta property="og:image" content="https://heinrich.dasancms.co.kr/og/on-premise.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={t('onpremise.pageTitle')} />
        <meta property="og:locale" content="ko_KR" />
        <meta property="og:locale:alternate" content="en_US" />
        <link rel="alternate" hrefLang="ko" href="https://heinrich.dasancms.co.kr/on-premise/" />
        <link rel="alternate" hrefLang="en" href="https://heinrich.dasancms.co.kr/on-premise/" />
        <link rel="alternate" hrefLang="x-default" href="https://heinrich.dasancms.co.kr/on-premise/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('onpremise.pageTitle')} />
        <meta name="twitter:description" content={t('onpremise.metaDescription')} />
        <meta name="twitter:image" content="https://heinrich.dasancms.co.kr/og/on-premise.png" />
      </Helmet>

      {/* Hero — 비대칭 스플릿 + 터미널 (장식 절제) */}
      <section
        className="relative overflow-hidden flex items-center bg-zinc-950 border-b border-white/[0.06]"
        aria-label={t('onpremise.heroAriaLabel')}
      >
        {/* 배경 사진 — 서버룸 다큐멘터리 톤 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <img
            src="/assets/hero/onpremise-hero-ambient.jpg"
            alt=""
            className="w-full h-full object-cover object-center opacity-35"
            loading="eager"
          />
          {/* 좌→우 다크 그라디언트로 좌측 텍스트 가독성 확보 */}
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/90 to-zinc-950/50" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-zinc-950 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 py-32 pt-44 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-[6fr_5fr] gap-12 lg:gap-16 items-center">
            {/* 좌측 — 텍스트 */}
            <div>
              {/* 배지들 — 단색·테두리만 */}
              <div className="flex flex-wrap gap-2 mb-8">
                {[
                  t('onpremise.badgeAirgap'),
                  t('onpremise.badgeOnPrem'),
                  t('onpremise.badgeNoExt'),
                ].map((badge) => (
                  <span
                    key={badge}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-zinc-300 border border-white/[0.10] bg-white/[0.02]"
                  >
                    <FiLock size={10} aria-hidden="true" className="text-zinc-500" />
                    {badge}
                  </span>
                ))}
              </div>

              {/* 헤드라인 — 단색 */}
              <h1
                className="font-bold tracking-tight leading-[1.1] mb-6 text-white"
                style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)' }}
              >
                {t('onpremise.heroHeadline1')}
                <br />
                <span className="text-zinc-400 font-medium">
                  {t('onpremise.heroHeadline2a')}
                  <br />
                  {t('onpremise.heroHeadline2b')}
                </span>
              </h1>

              <p className="text-base lg:text-lg text-zinc-400 mb-10 max-w-xl leading-relaxed">
                {renderWithBrand(t('onpremise.heroDescription'))}
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/contact?topic=enterprise">
                  <Button size="lg" rightIcon={<FiArrowRight aria-hidden="true" />} className="text-base px-8 w-full sm:w-auto">
                    {t('onpremise.ctaEnterprise')}
                  </Button>
                </Link>
                <a
                  href="https://dasancms.co.kr/dasan-ai/column/phase4-exam"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md text-base font-medium text-zinc-200 border border-white/[0.12] hover:border-white/[0.24] bg-white/[0.04] hover:bg-white/[0.08] transition-colors w-full sm:w-auto"
                >
                  {t('onpremise.ctaCertificate')}
                  <FiExternalLink size={14} aria-hidden="true" />
                </a>
              </div>
            </div>

            {/* 우측 — 터미널 모크업 (장식 backdrop 제거) */}
            <div className="relative">
              <TerminalMockup />
            </div>
          </div>
        </div>
      </section>

      {/* 풀블리드 에디토리얼 디바이더 — Air-Gapped 시각화 (Hero ↔ 왜 로컬) */}
      <section className="relative h-[200px] md:h-[260px] overflow-hidden bg-zinc-950" aria-hidden="true">
        <img
          src="/assets/section/onpremise-airgap.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-transparent to-zinc-950" />
      </section>

      {/* 섹션 1. 왜 로컬 LLM */}
      <section
        className="py-24 lg:py-32 bg-zinc-950"
        aria-labelledby="why-local-title"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...inView} className="text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-rose-500/15 border border-rose-500/25 text-zinc-900 dark:text-white uppercase tracking-widest mb-5">
              {t('onpremise.whyLocal.badge')}
            </span>
            <h2
              id="why-local-title"
              className="font-bold tracking-tight mb-5"
              style={{ fontSize: 'clamp(1.75rem, 4.5vw, 3rem)' }}
            >
              <span className="bg-gradient-to-r from-white to-zinc-300 bg-clip-text text-transparent">
                {t('onpremise.whyLocal.headline')}
              </span>
            </h2>
            <p className="text-base text-zinc-400 max-w-xl mx-auto leading-relaxed">
              {t('onpremise.whyLocal.description')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                icon: FiFileText,
                titleKey: 'onpremise.whyLocal.card1Title',
                bodyKey: 'onpremise.whyLocal.card1Body',
                gradient: 'from-rose-600/25 to-pink-600/10',
                border: 'border-rose-500/30',
                iconBg: 'bg-rose-500/15 border-rose-500/25',
                iconColor: 'text-rose-400',
              },
              {
                icon: FiShield,
                titleKey: 'onpremise.whyLocal.card2Title',
                bodyKey: 'onpremise.whyLocal.card2Body',
                gradient: 'from-amber-600/25 to-orange-600/10',
                border: 'border-amber-500/30',
                iconBg: 'bg-amber-500/15 border-amber-500/25',
                iconColor: 'text-amber-400',
              },
              {
                icon: FiLock,
                titleKey: 'onpremise.whyLocal.card3Title',
                bodyKey: 'onpremise.whyLocal.card3Body',
                gradient: 'from-indigo-600/25 to-amber-600/10',
                border: 'border-indigo-500/30',
                iconBg: 'bg-indigo-500/15 border-indigo-500/25',
                iconColor: 'text-indigo-400',
              },
            ].map((block, i) => (
              <motion.div
                key={block.titleKey}
                {...stagger(i)}
                className={`rounded-lg border bg-gradient-to-br p-7 flex flex-col gap-4 transition-transform duration-300 ${block.gradient} ${block.border}`}
              >
                <div className={`h-11 w-11 rounded-lg border flex items-center justify-center ${block.iconBg}`}>
                  <block.icon size={18} className={block.iconColor} aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-base font-bold mb-2 text-white">{t(block.titleKey)}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{renderWithBrand(t(block.bodyKey))}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" aria-hidden="true" />

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          섹션 2. 산업안전기사 합격 — 거대 숫자 메인
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section
        className="py-24 lg:py-32 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #030f0a 0%, #040c10 60%, #06091a 100%)' }}
        aria-labelledby="exam-title"
      >
        {/* 시험·인증 권위 톤 배경 사진 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <img
            src="/assets/section/onpremise-exam.jpg"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-15"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-zinc-950/85 to-zinc-950" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div {...inView} className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-md text-xs font-medium bg-amber-500/12 border border-amber-500/25 text-zinc-900 dark:text-white uppercase tracking-widest mb-5">
              {t('onpremise.exam.badge')}
            </span>
            <h2
              id="exam-title"
              className="font-bold tracking-tight mb-5"
              style={{ fontSize: 'clamp(1.75rem, 4.5vw, 3rem)' }}
            >
              <span className="text-white">
                {t('onpremise.exam.headline1')}
              </span>
              <span className="text-white"> {t('onpremise.exam.headline2')}</span>
            </h2>
            <p className="text-base text-zinc-400 max-w-2xl mx-auto leading-relaxed">
              {t('onpremise.exam.descriptionPre')}{' '}
              <span className="text-emerald-300 font-semibold">{t('onpremise.exam.descriptionMid')}</span>
              {t('onpremise.exam.descriptionSuf')}
            </p>
          </motion.div>

          {/* 거대한 숫자 2개 — 메인 강조 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 max-w-3xl mx-auto">
            <motion.div
              {...stagger(0)}
              className="rounded-lg border border-emerald-500/40 bg-gradient-to-br from-emerald-600/20 to-teal-600/10 p-8 text-center transition-transform duration-300 overflow-hidden"
            >
              <div className="tabular-nums tracking-tight font-bold text-emerald-400 mb-2 leading-none whitespace-nowrap"
                style={{ fontSize: 'clamp(2rem, 5vw, 3.75rem)' }}>
                109/120
              </div>
              <div className="text-sm font-semibold text-emerald-300 mb-1">{t('onpremise.exam.written')}</div>
              <div className="text-xs text-zinc-500">{t('onpremise.exam.writtenSub')}</div>
            </motion.div>
            <motion.div
              {...stagger(1)}
              className="rounded-lg border border-teal-500/40 bg-gradient-to-br from-teal-600/20 to-amber-600/10 p-8 text-center transition-transform duration-300 overflow-hidden"
            >
              <div className="tabular-nums tracking-tight font-bold text-teal-400 mb-2 leading-none whitespace-nowrap"
                style={{ fontSize: 'clamp(2rem, 5vw, 3.75rem)' }}>
                78/100
              </div>
              <div className="text-sm font-semibold text-teal-300 mb-1">{t('onpremise.exam.practical')}</div>
              <div className="text-xs text-zinc-500">{t('onpremise.exam.practicalSub')}</div>
            </motion.div>
          </div>

          {/* 보조 4개 카드 */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10 max-w-3xl mx-auto">
            {[
              { valueKey: 'onpremise.exam.card1Value', subKey: 'onpremise.exam.card1Sub', labelKey: 'onpremise.exam.card1Label', color: 'text-amber-400' },
              { valueKey: 'onpremise.exam.card2Value', subKey: 'onpremise.exam.card2Sub', labelKey: 'onpremise.exam.card2Label', color: 'text-amber-400' },
              { valueKey: 'onpremise.exam.card3Value', subKey: 'onpremise.exam.card3Sub', labelKey: 'onpremise.exam.card3Label', color: 'text-violet-400' },
              { valueKey: 'onpremise.exam.card4Value', subKey: 'onpremise.exam.card4Sub', labelKey: 'onpremise.exam.card4Label', color: 'text-amber-400' },
            ].map((card, i) => {
              return (
                <motion.div
                  key={card.labelKey}
                  {...stagger(i + 2)}
                  className="rounded-lg border border-white/[0.08] bg-gradient-to-br from-zinc-800/40 to-zinc-900/40 p-4 text-center"
                >
                  <div className={`tracking-tight text-2xl font-bold whitespace-nowrap ${card.color} mb-1`}>{t(card.valueKey)}</div>
                  <div className="text-[10px] text-zinc-600 mb-1">{t(card.subKey)}</div>
                  <div className="text-xs font-medium text-zinc-400">{t(card.labelKey)}</div>
                </motion.div>
              )
            })}
          </div>

          {/* 3가지 차별점 */}
          <motion.div {...inView} className="rounded-lg border border-white/[0.08] bg-gradient-to-br from-zinc-900/80 to-zinc-950/80 p-7 lg:p-9 mb-8 max-w-3xl mx-auto">
            <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
              {renderWithBrand(t('onpremise.exam.diffIntro'))}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                { icon: FiLock, titleKey: 'onpremise.exam.diff1Title', descKey: 'onpremise.exam.diff1Desc', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
                { icon: FiFileText, titleKey: 'onpremise.exam.diff2Title', descKey: 'onpremise.exam.diff2Desc', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
                { icon: FiShield, titleKey: 'onpremise.exam.diff3Title', descKey: 'onpremise.exam.diff3Desc', color: 'text-violet-400', bg: 'bg-violet-500/10 border-violet-500/20' },
              ].map((pt) => (
                <div key={pt.titleKey} className="flex items-start gap-3">
                  <div className={`h-9 w-9 rounded-md border flex items-center justify-center shrink-0 ${pt.bg}`}>
                    <pt.icon size={15} className={pt.color} aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white mb-1">{t(pt.titleKey)}</div>
                    <div className="text-xs text-zinc-400 leading-relaxed">{t(pt.descKey)}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div {...inView} className="text-center">
            <a
              href="https://dasancms.co.kr/dasan-ai/column/phase4-exam"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium text-zinc-900 dark:text-white border border-emerald-500/30 hover:border-emerald-500/60 hover:text-zinc-900 dark:text-white hover:bg-emerald-500/08 transition-all duration-200"
            >
              {t('onpremise.exam.moreAnalysis')}
              <FiExternalLink size={13} aria-hidden="true" />
            </a>
          </motion.div>
        </div>
      </section>

      <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" aria-hidden="true" />

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          섹션 3. 이미지 분석 비교 — 컬러 카드 덱
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section
        className="py-24 lg:py-32 bg-zinc-950"
        aria-labelledby="comparison-title"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...inView} className="text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-amber-500/15 border border-amber-500/25 text-zinc-900 dark:text-white uppercase tracking-widest mb-5">
              {t('onpremise.comparison.badge')}
            </span>
            <h2
              id="comparison-title"
              className="font-bold tracking-tight mb-5"
              style={{ fontSize: 'clamp(1.75rem, 4.5vw, 3rem)' }}
            >
              <span className="text-white">{t('onpremise.comparison.headline1')}</span>{' '}
              <span className="text-amber-300">
                {t('onpremise.comparison.headline2')}
              </span>
            </h2>
            <p className="text-base text-zinc-400 max-w-xl mx-auto leading-relaxed">
              {t('onpremise.comparison.description')}
            </p>
          </motion.div>

          {/* 3칼럼 카드 덱 — Heinrich 카드 앞에 + tilt */}
          <div className="flex flex-col lg:flex-row gap-5 lg:gap-0 justify-center items-end mb-12">
            {/* GPT 카드 — 기울어진 뒤쪽 */}
            <motion.div
              {...stagger(0)}
              className="w-full lg:w-72 lg:-mr-8 lg:rotate-[-3deg] lg:translate-y-4"
            >
              <div className="rounded-lg border border-zinc-600/30 bg-gradient-to-br from-zinc-800/50 to-zinc-900/50 p-6 h-full">
                <div className="text-xs font-semibold text-zinc-500 mb-4 uppercase tracking-widest">{t('onpremise.comparison.gptLabel')}</div>
                {COMPARISON_ROW_KEYS.map((row) => (
                  <div key={row.itemKey} className="flex items-start gap-2 py-2 border-b border-white/[0.04] last:border-0">
                    <FiX size={12} className="text-rose-500 shrink-0 mt-0.5" aria-hidden="true" />
                    <div>
                      <div className="text-[10px] text-zinc-600 mb-0.5">{t(row.itemKey)}</div>
                      <div className="text-xs text-zinc-500 line-through decoration-zinc-600/40">{t(row.gptKey)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Heinrich 카드 — 중앙 앞쪽 + 컬러 */}
            <motion.div
              {...stagger(1)}
              className="w-full lg:w-80 lg:z-10 relative"
            >
              {/* glow */}
              <div className="absolute -inset-px rounded-lg bg-gradient-to-br from-amber-500/50 to-amber-500/30 blur-sm" aria-hidden="true" />
              <div className="relative rounded-lg border border-amber-400/50 bg-gradient-to-br from-amber-600/25 to-amber-600/15 p-6 shadow-md shadow-amber-500/20">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-xs font-bold text-amber-300 uppercase tracking-widest">{renderWithBrand(t('onpremise.comparison.saifeLabel'))}</div>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-lg bg-amber-500/30 border border-amber-500/40 text-zinc-900 dark:text-white">{t('onpremise.comparison.recommended')}</span>
                </div>
                {COMPARISON_ROW_KEYS.map((row) => (
                  <div key={row.itemKey} className="flex items-start gap-2 py-2 border-b border-amber-500/10 last:border-0">
                    <FiCheck size={12} className="text-amber-400 shrink-0 mt-0.5" aria-hidden="true" />
                    <div>
                      <div className="text-[10px] text-amber-300/60 mb-0.5">{t(row.itemKey)}</div>
                      <div className="text-xs font-semibold text-amber-200">{t(row.saifeKey)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Gemini 카드 — 반대 기울기 */}
            <motion.div
              {...stagger(2)}
              className="w-full lg:w-72 lg:-ml-8 lg:rotate-[3deg] lg:translate-y-4"
            >
              <div className="rounded-lg border border-zinc-600/30 bg-gradient-to-br from-zinc-800/50 to-zinc-900/50 p-6 h-full">
                <div className="text-xs font-semibold text-zinc-500 mb-4 uppercase tracking-widest">{t('onpremise.comparison.geminiLabel')}</div>
                {COMPARISON_ROW_KEYS.map((row) => (
                  <div key={row.itemKey} className="flex items-start gap-2 py-2 border-b border-white/[0.04] last:border-0">
                    <FiX size={12} className="text-rose-500 shrink-0 mt-0.5" aria-hidden="true" />
                    <div>
                      <div className="text-[10px] text-zinc-600 mb-0.5">{t(row.itemKey)}</div>
                      <div className="text-xs text-zinc-500 line-through decoration-zinc-600/40">{t(row.geminiKey)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* 연쇄고장 다이어그램 */}
          <motion.div {...inView} className="rounded-lg border border-amber-500/20 bg-gradient-to-br from-amber-600/10 to-orange-600/05 p-7 lg:p-9 mb-8 max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-5">
              <FiAlertTriangle size={16} className="text-amber-400" aria-hidden="true" />
              <h3 className="text-sm font-bold text-white">{renderWithBrand(t('onpremise.comparison.cascadeTitle'))}</h3>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {[
                t('onpremise.comparison.cascadeStep1'),
                '→',
                t('onpremise.comparison.cascadeStep2'),
                '→',
                t('onpremise.comparison.cascadeStep3'),
                '→',
                t('onpremise.comparison.cascadeStep4'),
                '→',
                t('onpremise.comparison.cascadeStep5'),
              ].map((step, i) => (
                step === '→' ? (
                  <span key={i} className="text-amber-600/60 font-medium" aria-hidden="true">→</span>
                ) : (
                  <span
                    key={i}
                    className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium border border-amber-500/20 bg-amber-500/10 text-zinc-900 dark:text-white"
                  >
                    {step}
                  </span>
                )
              ))}
            </div>
            <p className="mt-5 text-xs text-zinc-600">
              * <span className="text-zinc-500 font-medium">{t('onpremise.comparison.cascadeFootnote')}</span>
            </p>
          </motion.div>

          <motion.div {...inView} className="text-center">
            <a
              href="https://dasancms.co.kr/dasan-ai/column/phase1-comparison"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium text-amber-300 border border-amber-500/30 hover:border-amber-500/60 hover:text-amber-200 transition-all duration-200"
            >
              {t('onpremise.comparison.fullCompare')}
              <FiExternalLink size={13} aria-hidden="true" />
            </a>
          </motion.div>
        </div>
      </section>

      <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" aria-hidden="true" />

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          섹션 4. 배포 옵션 — 컬러 카드
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section
        className="py-24 lg:py-32 bg-[#060a14]"
        aria-labelledby="deployment-title"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...inView} className="text-center mb-14">
            <h2
              id="deployment-title"
              className="font-bold tracking-tight mb-5"
              style={{ fontSize: 'clamp(1.75rem, 4.5vw, 3rem)' }}
            >
              <span className="bg-gradient-to-r from-white to-zinc-300 bg-clip-text text-transparent">{t('onpremise.deployment.headline')}</span>
            </h2>
            <p className="text-base text-zinc-400 max-w-xl mx-auto">
              {t('onpremise.deployment.description')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                tier: 'Cloud',
                tierKey: 'onpremise.deployment.opt1Tier',
                engineKey: 'onpremise.deployment.opt1Engine',
                descKey: 'onpremise.deployment.opt1Desc',
                featureKeys: [
                  'onpremise.deployment.opt1F1',
                  'onpremise.deployment.opt1F2',
                  'onpremise.deployment.opt1F3',
                  'onpremise.deployment.opt1F4',
                ],
                recKey: 'onpremise.deployment.opt1Rec',
                badgeKey: 'onpremise.deployment.opt1Badge',
                gradient: 'from-amber-600/20 to-amber-900/15',
                border: 'border-amber-500/25',
                accent: 'text-amber-400',
                badgeBg: 'bg-amber-500/15 border-amber-500/25',
              },
              {
                tier: 'Private Cloud',
                tierKey: 'onpremise.deployment.opt2Tier',
                engineKey: 'onpremise.deployment.opt2Engine',
                descKey: 'onpremise.deployment.opt2Desc',
                featureKeys: [
                  'onpremise.deployment.opt2F1',
                  'onpremise.deployment.opt2F2',
                  'onpremise.deployment.opt2F3',
                  'onpremise.deployment.opt2F4',
                  'onpremise.deployment.opt2F5',
                ],
                recKey: 'onpremise.deployment.opt2Rec',
                badgeKey: 'onpremise.deployment.opt2Badge',
                gradient: 'from-violet-600/20 to-purple-900/15',
                border: 'border-violet-500/25',
                accent: 'text-violet-400',
                badgeBg: 'bg-violet-500/15 border-violet-500/25',
              },
              {
                tier: 'On-Premise',
                tierKey: 'onpremise.deployment.opt3Tier',
                engineKey: 'onpremise.deployment.opt3Engine',
                descKey: 'onpremise.deployment.opt3Desc',
                featureKeys: [
                  'onpremise.deployment.opt3F1',
                  'onpremise.deployment.opt3F2',
                  'onpremise.deployment.opt3F3',
                  'onpremise.deployment.opt3F4',
                  'onpremise.deployment.opt3F5',
                  'onpremise.deployment.opt3F6',
                ],
                recKey: 'onpremise.deployment.opt3Rec',
                badgeKey: 'onpremise.deployment.opt3Badge',
                gradient: 'from-emerald-600/20 to-teal-900/15',
                border: 'border-emerald-500/25',
                accent: 'text-emerald-400',
                badgeBg: 'bg-emerald-500/15 border-emerald-500/25',
              },
            ].map((opt, i) => (
              <motion.div
                key={opt.tier}
                {...stagger(i)}
                className={`rounded-lg border bg-gradient-to-br transition-all duration-300 p-7 flex flex-col gap-5 ${opt.gradient} ${opt.border}`}
              >
                <div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-semibold border ${opt.badgeBg} ${opt.accent} mb-3`}>
                    {t(opt.badgeKey)}
                  </span>
                  <h3 className="text-xl font-bold text-white mb-1"><Brand /> {t(opt.tierKey)}</h3>
                  <div className={`text-xs ${opt.accent} mb-3 font-medium`}>{t(opt.engineKey)}</div>
                  <p className="text-sm text-zinc-400 leading-relaxed">{t(opt.descKey)}</p>
                </div>

                <ul className="flex flex-col gap-2 flex-1">
                  {opt.featureKeys.map((fk) => (
                    <li key={fk} className="flex items-start gap-2 text-xs text-zinc-300">
                      <FiCheck size={12} className={`${opt.accent} shrink-0 mt-0.5`} aria-hidden="true" />
                      {t(fk)}
                    </li>
                  ))}
                </ul>

                <div className={`pt-3 border-t border-white/[0.06]`}>
                  <div className="text-[10px] text-zinc-500 mb-1">{t('onpremise.deployment.recommendedLabel')}</div>
                  <div className={`text-xs font-semibold ${opt.accent}`}>{t(opt.recKey)}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" aria-hidden="true" />

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          섹션 5. 도입 프로세스
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section
        className="py-24 lg:py-32 bg-zinc-950"
        aria-labelledby="process-title"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...inView} className="text-center mb-14">
            <h2
              id="process-title"
              className="font-bold tracking-tight mb-5"
              style={{ fontSize: 'clamp(1.75rem, 4.5vw, 3rem)' }}
            >
              <span className="bg-gradient-to-r from-white to-zinc-300 bg-clip-text text-transparent">{t('onpremise.process.headline')}</span>
            </h2>
            <p className="text-base text-zinc-400 max-w-xl mx-auto">
              {t('onpremise.process.description')}
            </p>
          </motion.div>

          <div className="relative">
            <div
              className="hidden lg:block absolute top-8 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-white/[0.10] to-transparent"
              aria-hidden="true"
            />
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {[
                { step: '01', icon: FiUsers, titleKey: 'onpremise.process.step1Title', durationKey: 'onpremise.process.step1Duration', descKey: 'onpremise.process.step1Desc', color: 'from-amber-500 to-amber-500', textColor: 'text-amber-400' },
                { step: '02', icon: FiServer, titleKey: 'onpremise.process.step2Title', durationKey: 'onpremise.process.step2Duration', descKey: 'onpremise.process.step2Desc', color: 'from-violet-500 to-purple-500', textColor: 'text-violet-400' },
                { step: '03', icon: FiFileText, titleKey: 'onpremise.process.step3Title', durationKey: 'onpremise.process.step3Duration', descKey: 'onpremise.process.step3Desc', color: 'from-amber-500 to-orange-500', textColor: 'text-amber-400' },
                { step: '04', icon: FiZap, titleKey: 'onpremise.process.step4Title', durationKey: 'onpremise.process.step4Duration', descKey: 'onpremise.process.step4Desc', color: 'from-emerald-500 to-teal-500', textColor: 'text-emerald-400' },
              ].map((phase, i) => (
                <motion.div
                  key={phase.step}
                  {...stagger(i)}
                  className="flex flex-col items-center text-center lg:items-start lg:text-left"
                >
                  <div className="relative mb-5">
                    <div className={`h-16 w-16 rounded-lg bg-gradient-to-br ${phase.color} bg-opacity-20 flex items-center justify-center`}
                      style={{ background: `linear-gradient(135deg, rgba(var(--tw-gradient-stops)), transparent)` }}>
                      <div className={`h-16 w-16 rounded-lg border border-white/[0.12] bg-white/[0.06] flex items-center justify-center`}>
                        <phase.icon size={24} className={phase.textColor} aria-hidden="true" />
                      </div>
                    </div>
                    <span className={`absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full bg-gradient-to-br ${phase.color} text-[10px] font-bold text-white flex items-center justify-center shadow-lg`}>
                      {phase.step}
                    </span>
                  </div>
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-[10px] font-semibold bg-white/[0.06] border border-white/[0.08] ${phase.textColor} mb-3`}>
                    <FiClock size={10} aria-hidden="true" />
                    {t(phase.durationKey)}
                  </div>
                  <h3 className="text-base font-bold text-white mb-2">{t(phase.titleKey)}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{t(phase.descKey)}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          섹션 (신규). 회사 양식 그대로 자동 생성 — Enterprise 맞춤 커스터마이즈
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section
        className="relative py-24 lg:py-32 overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #060a14 0%, #08101c 50%, #060a14 100%)' }}
        aria-label="회사 양식 자동 생성 맞춤 커스터마이즈"
      >
        {/* 배경 amber glow */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <motion.div
            className="absolute top-1/3 left-1/4 w-[40rem] h-[25rem] rounded-full blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.10) 0%, rgba(217,119,6,0.05) 40%, transparent 70%)' }}
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          {/* 헤더 */}
          <motion.div {...inView} className="text-center mb-12 lg:mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-semibold mb-5">
              <FiFileText size={12} aria-hidden="true" />
              Enterprise Customization
            </div>
            <h2
              className="font-bold tracking-tight mb-5 leading-tight"
              style={{ fontSize: 'clamp(1.75rem, 4.5vw, 3rem)' }}
            >
              <span className="text-white">우리 회사 양식 그대로, </span>
              <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">
                AI 가 자동으로 작성합니다
              </span>
            </h2>
            <p className="text-base lg:text-lg text-zinc-400 max-w-3xl mx-auto leading-relaxed">
              JSA · 위험성평가 · TBM · 작업허가서 · 안전교육 자료 — 회사가 이미 사용 중인
              <span className="text-amber-300 font-semibold"> 결재 라인 · 로고 · 양식번호 · 표현 규칙</span>
              을 그대로 유지한 채 AI 가 작성합니다. 기존 양식 변경 없이 사내 결재와 감독관 점검을
              그대로 진행할 수 있습니다.
            </p>
          </motion.div>

          {/* 지원 문서 9종 grid */}
          <motion.div {...inView} className="mb-14 lg:mb-20">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                <span className="h-px w-8 bg-zinc-700"></span>
                지원 문서
                <span className="h-px w-8 bg-zinc-700"></span>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {[
                'JSA 작업안전분석',
                '위험성평가',
                'TBM 일지',
                '작업허가서 (PTW)',
                '산업재해조사표',
                '안전교육 일지·출석부',
                '안전교육 자료 (PPTX·영상)',
                '시정지시서·점검표',
                '회사 자체 양식 (협의)',
              ].map((name) => (
                <div
                  key={name}
                  className="px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-xs text-zinc-300 text-center hover:border-amber-500/30 hover:bg-amber-500/[0.04] transition-colors"
                >
                  {name}
                </div>
              ))}
            </div>
          </motion.div>

          {/* 도입 절차 4단계 */}
          <motion.div {...inView}>
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                <span className="h-px w-8 bg-zinc-700"></span>
                도입 절차 · 평균 3 ~ 4 개월
                <span className="h-px w-8 bg-zinc-700"></span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  step: 1, period: '1 ~ 2 주',
                  title: '요구사항 분석',
                  desc: '담당자·안전팀·결재권자 인터뷰. 현재 사용 중인 모든 양식 수집 + 매핑 설계.',
                },
                {
                  step: 2, period: '3 ~ 4 주',
                  title: 'AI 모델 커스터마이즈',
                  desc: '회사 데이터로 fine-tuning. 사내 표현 규칙·약어·결재 라인 자동 적용 학습.',
                },
                {
                  step: 3, period: '4 ~ 6 주',
                  title: '베타 검수',
                  desc: '실 데이터 30 ~ 50건 생성. 담당자 검수 → 피드백 → 재학습 3 ~ 5 라운드.',
                },
                {
                  step: 4, period: '2 ~ 3 주',
                  title: '사내 교육·배포',
                  desc: '사용자 교육. 부서·사업장 단위 단계적 롤아웃 + 안정화 모니터링.',
                },
              ].map((s, i) => (
                <motion.div
                  key={s.step}
                  {...stagger(i)}
                  className="relative p-5 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-amber-500/30 transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-sm font-bold">
                      {s.step}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] text-zinc-500 font-semibold">
                      <FiClock size={10} aria-hidden="true" />
                      {s.period}
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-white mb-2">{s.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* 도입 효과 */}
          <motion.div {...inView} className="mt-14 lg:mt-20">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { title: '결재 라인 그대로', desc: '기존 결재 시스템 · 그룹웨어 첨부 양식 무변경' },
                { title: '감독관 점검 대응', desc: '근로감독관 점검 시 사내 표준 양식 그대로 제출' },
                { title: '복수 사업장 지원', desc: '본사·지사·협력업체별 양식 차이 자동 매칭' },
                { title: 'AI 품질 동일', desc: 'KOSHA 규정·산업안전보건법 인용 표준 버전 수준' },
              ].map((b) => (
                <div
                  key={b.title}
                  className="p-4 rounded-lg bg-amber-500/[0.04] border border-amber-500/15"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <FiCheck size={14} className="text-amber-400 shrink-0" aria-hidden="true" />
                    <span className="text-sm font-bold text-white">{b.title}</span>
                  </div>
                  <p className="text-xs text-zinc-400 leading-relaxed pl-6">{b.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div {...inView} className="mt-14 lg:mt-20 text-center">
            <p className="text-sm text-zinc-400 mb-5">
              현재 사용 중인 양식을 첨부해 도입 문의를 보내주시면, 영업일 기준 5일 이내에
              1차 분석 결과와 도입 일정 · 예상 견적 안내드립니다.
            </p>
            <Link to="/contact?topic=enterprise">
              <Button size="lg" rightIcon={<FiArrowRight aria-hidden="true" />}
                      className="text-base px-10 bg-amber-500 hover:bg-amber-600 border-amber-500">
                도입 문의
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          섹션 6. 최종 CTA — 풀블리드 emerald 그라디언트
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section
        className="relative py-32 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #030f0a 0%, #041510 40%, #05120f 100%)' }}
        aria-label={t('onpremise.finalCta.ariaLabel')}
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60rem] h-[35rem] rounded-full blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.12) 0%, rgba(6,182,212,0.07) 40%, transparent 70%)' }}
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute top-0 right-1/4 w-[30rem] h-[20rem] rounded-full blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)' }}
            animate={{ x: [0, 20, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2
              className="font-bold tracking-tight mb-5 leading-tight"
              style={{ fontSize: 'clamp(2rem, 5vw, 4rem)' }}
            >
              <span className="text-white">{t('onpremise.finalCta.headline1')}</span>
              <br />
              <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-amber-400 bg-clip-text text-transparent">
                {t('onpremise.finalCta.headline2')}
              </span>
            </h2>
            <p className="text-base lg:text-lg text-zinc-400 mb-10 max-w-xl mx-auto leading-relaxed">
              {t('onpremise.finalCta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact?topic=enterprise">
                <Button size="lg" rightIcon={<FiArrowRight aria-hidden="true" />} className="text-base px-10 w-full sm:w-auto">
                  {t('onpremise.finalCta.request')}
                </Button>
              </Link>
              <a
                href="https://dasancms.co.kr/dasan-ai/column/phase4-exam"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md text-base font-medium text-zinc-900 dark:text-white border border-emerald-500/30 hover:border-emerald-500/60 hover:text-zinc-900 dark:text-white bg-transparent hover:bg-emerald-500/08 transition-all duration-200 w-full sm:w-auto"
              >
                <FiAward size={16} aria-hidden="true" />
                {t('onpremise.finalCta.examProof')}
                <FiExternalLink size={13} aria-hidden="true" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}
